import React, { ReactNode, useEffect, useState } from 'react'

import { useFetcher } from 'react-router'

import { MODE } from './constants'
import { DarkModeContext } from './context'
import { setPreferColorSchemeCookie } from './helpers'
import { ACTION } from '~/services/actions'

interface DarkModeProviderProps {
  children: ReactNode,
  defaultMode?: MODE,
  preferredColorScheme?: MODE.LIGHT | MODE.DARK,
  disableColorSchemeListener?: boolean
}

export const DarkModeProvider = (
  {
    children,
    defaultMode = MODE.SYSTEM,
    preferredColorScheme,
    disableColorSchemeListener
  }: DarkModeProviderProps
) => {

  const [mode, setMode] = useState<MODE>(defaultMode)

  const [preferredColorSchemeValue, setPreferredColorScheme] = useState<MODE.LIGHT | MODE.DARK>(preferredColorScheme || MODE.LIGHT)

  const fetcher = useFetcher?.()

  const setGlobalMode = (newMode) => {
    setMode(newMode)
    let preferredColorScheme
    if (typeof window !== 'undefined') {
      window['ENV']['mode'] = newMode
      window['setSystemTheme']?.()
      preferredColorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? MODE.DARK : MODE.LIGHT
    }
    fetcher?.submit?.({ mode: newMode, preferredColorScheme }, { method: 'POST', action: ACTION.SET_MODE(), })
  }

  const setLocalMode = (newMode) => {
    setMode(newMode)
    if (typeof window !== 'undefined') {
      window['ENV']['mode'] = newMode
      window['setSystemTheme']?.()
    }
  }

  useEffect(() => {
    if (disableColorSchemeListener) { return }
    // Preferred color scheme listener - update cookie if preferred color scheme changes
    if (typeof window !== 'undefined') {
      const windowPreferredColorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? MODE.DARK : MODE.LIGHT

      if (preferredColorScheme !== windowPreferredColorScheme && !disableColorSchemeListener) {
        setPreferredColorScheme(windowPreferredColorScheme)
        setPreferColorSchemeCookie({ mode, preferredColorScheme: windowPreferredColorScheme, submitFunction: fetcher?.submit })
      }
    }
  }, [])

  const isDark = () => {
    if (typeof window !== 'undefined') {
      return mode === MODE.DARK || (window as any)?.ENV?.mode === MODE.DARK || ((window as any)?.ENV?.mode === MODE.SYSTEM && window?.matchMedia('(prefers-color-scheme: dark)')?.matches)
    }
    return mode === MODE.DARK || mode === MODE.SYSTEM && preferredColorSchemeValue === MODE.DARK
  }

  const isSystem = () => {
    if (typeof window !== 'undefined') {
      return mode === MODE.SYSTEM || (window as any)?.ENV?.mode === MODE.SYSTEM
    }
    return mode === MODE.SYSTEM
  }

  return <DarkModeContext.Provider value={{ mode, setMode: setGlobalMode, isDark, setLocalMode, isSystem }}><React.Fragment key={preferredColorSchemeValue}>{children}</React.Fragment></DarkModeContext.Provider>
}